import { getTg } from "./tg";
import type { Me, Employee, Lang, AccessGrant, MonthStatus, PanelLoad, RetailMonth, RankingMonth } from "./types";

const API_BASE = import.meta.env.VITE_API_BASE || "";

async function call<T>(path: string, init: RequestInit = {}): Promise<T> {
  const initData = getTg()?.initData ?? "";
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      "X-Init-Data": initData,
      ...(init.body ? { "Content-Type": "application/json" } : {}),
      ...init.headers,
    },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({} as any));
    const err = new Error((body as any).error || `HTTP_${res.status}`);
    if ((body as any).error === "MAINTENANCE") {
      (err as any).maintenance = {
        message_ru: (body as any).message_ru || "",
        message_uk: (body as any).message_uk || "",
      };
    }
    throw err;
  }
  return res.json() as Promise<T>;
}

export const apiGet   = <T>(path: string) => call<T>(path);
export const apiPatch = <T>(path: string, body: unknown) => call<T>(path, { method: "PATCH", body: JSON.stringify(body) });
export const apiPost  = <T>(path: string, body: unknown) => call<T>(path, { method: "POST",  body: JSON.stringify(body) });
export const apiDelete = <T>(path: string) => call<T>(path, { method: "DELETE" });

export const getMe     = () => apiGet<Me>("/api/me");
export const patchLang = (lang: Lang) => apiPatch<{ ok: true; lang: Lang }>("/api/me/lang", { lang });

export const getAvailableMonths = () => apiGet<{ months: string[]; statuses?: MonthStatus[] }>("/api/months");

// --- админ ---
export const getEmployees = () => apiGet<{ employees: Employee[] }>("/api/employees");
export const getAllEmployees = () => apiGet<{ employees: Employee[] }>("/api/employees?include_archived=1");
export const createEmployee = (personId: string, displayName: string) =>
  apiPost<{ employee: Employee }>("/api/employees", { person_id: personId, display_name: displayName });
export const setEmployeeActive = (personId: string, active: boolean) =>
  apiPatch<{ ok: true; person_id: string; active: 0 | 1 }>(`/api/employees/${encodeURIComponent(personId)}`, { active });
export const updateEmployee = (personId: string, patch: { person_id?: string; display_name?: string; view_store?: string | null }) =>
  apiPatch<{ ok: true; person_id: string; active: 0 | 1; employee: Employee }>(`/api/employees/${encodeURIComponent(personId)}`, patch);

export const getAccess = () => apiGet<{ grants: AccessGrant[] }>("/api/access");
export const createAccess = (phone: string, personId: string) =>
  apiPost<{ grant: AccessGrant }>("/api/access", { phone, person_id: personId });
export const deleteAccess = (id: number) => apiDelete<{ ok: true }>(`/api/access/${id}`);

export const getLoads = () => apiGet<{ loads: PanelLoad[] }>("/api/loads");

/** Данные месяца: сотруднице — только свои строки, админу — все (этап 3 строит экран на этом). */
export const getRetailMonth = (month: string) => apiGet<RetailMonth>(`/api/retail/${encodeURIComponent(month)}`);

/** Рейтинг внутри магазина: сотруднице — свой магазин анонимно; владельцу — все магазины с именами (или ?store=). */
export const getRanking = (month: string, store?: string | null) =>
  apiGet<RankingMonth>(`/api/retail/${encodeURIComponent(month)}/ranking${store ? `?store=${encodeURIComponent(store)}` : ""}`);

export type Settings = Record<string, string>;
export const getSettings = () => apiGet<{ settings: Settings }>("/api/settings");
export const patchSettings = (patch: Partial<Settings>) =>
  apiPatch<{ ok: true }>("/api/settings", patch);

export interface AccessEvent {
  id: number;
  user_id: number | null;
  phone: string | null;
  event_type: "login_ok" | "login_denied";
  at: number;
  meta: string | null;
  user_phone: string | null;
  first_name: string | null;
  last_name: string | null;
  role: string | null;
}
export const getEvents = (limit = 100) =>
  apiGet<{ events: AccessEvent[] }>(`/api/events?limit=${limit}`);
